import { forwardRef } from "react";
import { Button, type ButtonProps, type ButtonSize } from "./button";
import { ACTIONS, type ActionKey, type ActionDescriptor } from "@/lib/actions-catalog/catalog";

/**
 * Canonical action button.
 *
 * Renders a `<Button>` from the action catalog so every "Approve" /
 * "Run fix" / "Send" / "Delete" across the app uses identical
 * variant + icon + tier without each page picking them itself.
 *
 *   <ActionButton action="approve" onClick={…} />
 *   <ActionButton action="delete"  onClick={…} label="Remove tenant" />
 *   <ActionButton action="runFix"  onClick={…} loading={busy} size="lg" />
 *
 * The `label` prop overrides the catalog default (every page has its
 * own i18n string); icon / variant / destructive are owned by the
 * catalog and CANNOT be overridden — that's the point of canonical
 * actions.
 *
 * Pages that need a non-canonical button (page-specific "Connect
 * Slack", "Reinstall integration") still reach for the bare
 * `<Button>` directly. The action catalog is for the closed set of
 * verbs operators see across queues.
 */
export type ActionButtonProps = Omit<ButtonProps, "variant" | "icon" | "children"> & {
  action: ActionKey;
  /** Override the catalog's default label (almost always the i18n
   *  string, e.g. `tr("issuesDetail.actions.approve")`). */
  label?: string;
  /** Default "md". Primary CTAs in modal footers / page headers
   *  typically pass "lg"; row-level actions use "sm". */
  size?: ButtonSize;
};

export const ActionButton = forwardRef<HTMLButtonElement, ActionButtonProps>(
  function ActionButton({ action, label, size = "md", ...rest }, ref) {
    const desc: ActionDescriptor = ACTIONS[action];
    return (
      <Button
        ref={ref}
        variant={desc.variant}
        size={size}
        icon={desc.icon}
        {...rest}
      >
        {label ?? desc.defaultLabel}
      </Button>
    );
  },
);
